import { useAuth } from './useAuth';
import { Role } from '../types';

export function usePermission() {
  const { user, isAuthenticated } = useAuth();

  const hasRole = (...roles: Role[]): boolean => {
    if (!user) return false;
    return roles.includes(user.role);
  };

  const isSuperAdmin = (): boolean => hasRole(Role.PLATFORM_SUPER_ADMIN);
  const isTenantAdmin = (): boolean => hasRole(Role.TENANT_ADMIN);
  const isManager = (): boolean => hasRole(Role.MANAGER);
  const isEmployee = (): boolean => hasRole(Role.EMPLOYEE);

  const isAdmin = (): boolean => hasRole(Role.PLATFORM_SUPER_ADMIN, Role.TENANT_ADMIN);

  const canManageTenants = (): boolean => isSuperAdmin();
  const canManagePlans = (): boolean => isSuperAdmin();
  const canManageEmployees = (): boolean => hasRole(Role.PLATFORM_SUPER_ADMIN, Role.TENANT_ADMIN);
  const canManageCustomers = (): boolean => hasRole(Role.PLATFORM_SUPER_ADMIN, Role.TENANT_ADMIN);
  const canManageGeofences = (): boolean => hasRole(Role.PLATFORM_SUPER_ADMIN, Role.TENANT_ADMIN);
  const canAssignVisits = (): boolean => hasRole(Role.TENANT_ADMIN, Role.MANAGER);
  const canViewReports = (): boolean => hasRole(Role.PLATFORM_SUPER_ADMIN, Role.TENANT_ADMIN, Role.MANAGER);
  const canViewLiveMap = (): boolean => hasRole(Role.TENANT_ADMIN, Role.MANAGER);

  const getHomeRoute = (): string => {
    if (!user) return '/login';
    switch (user.role) {
      case Role.PLATFORM_SUPER_ADMIN:
        return '/admin/dashboard';
      case Role.TENANT_ADMIN:
        return '/tenant/dashboard';
      case Role.MANAGER:
        return '/manager/dashboard';
      case Role.EMPLOYEE:
        // Employee web console is not available yet — stay on login.
        return '/login';
      default:
        return '/login';
    }
  };

  return {
    user,
    isAuthenticated,
    hasRole,
    isSuperAdmin,
    isTenantAdmin,
    isManager,
    isEmployee,
    isAdmin,
    canManageTenants,
    canManagePlans,
    canManageEmployees,
    canManageCustomers,
    canManageGeofences,
    canAssignVisits,
    canViewReports,
    canViewLiveMap,
    getHomeRoute,
  };
}
